import type { Instance } from "./model";

// The standing is one evidence-updated score: a read (baseline or latest
// re-rate) nudged by decayed, signed, magnitude-weighted events SINCE that read.
// Deliberately simple and explainable; the band is "indicative reliability",
// never a statistical confidence interval.
export interface StandingConfig {
  halfLifeDays: number;
  priorWeight: number; // anchoring strength (kappa); higher = small events move less
  gain: number; // how far full, consistent evidence can move the point
  bandK: number; // base band half-width
  dispGain: number; // how much disagreement widens the band
}

export const DEFAULT_STANDING_CONFIG: StandingConfig = {
  halfLifeDays: 180,
  priorWeight: 5,
  gain: 4,
  bandK: 1.4,
  dispGain: 1.5,
};

export interface Standing {
  read: number; // the current baseline / latest re-rate
  point: number; // the evidence-updated score (feeds the lens)
  band: { low: number; high: number } | null; // shown only when events exist
  eventCount: number;
}

const DAY_MS = 86_400_000;
const clamp = (x: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, x));

// `events` should already be filtered to those since the current read's anchor.
export function computeStanding(
  read: number,
  events: readonly Instance[],
  now: number,
  cfg: StandingConfig = DEFAULT_STANDING_CONFIG,
): Standing {
  const n = events.length;
  if (n === 0) return { read, point: read, band: null, eventCount: 0 };

  let signed = 0;
  let mass = 0;
  for (const e of events) {
    const ageDays = Math.max(0, (now - e.occurred_at) / DAY_MS);
    const decay = Math.pow(0.5, ageDays / cfg.halfLifeDays);
    const weighted = e.magnitude * decay;
    signed += (e.valence === "+" ? 1 : -1) * weighted;
    mass += weighted;
  }

  const point = clamp(read + cfg.gain * (signed / (mass + cfg.priorWeight)), 1, 5);
  const dispersion = mass > 0 ? 1 - Math.abs(signed) / mass : 0;
  const halfWidth = (cfg.bandK / Math.sqrt(n)) * (1 + cfg.dispGain * dispersion);
  const band = { low: clamp(point - halfWidth, 1, 5), high: clamp(point + halfWidth, 1, 5) };

  return { read, point, band, eventCount: n };
}

export function indexInstances(instances: Instance[]): Map<string, Instance[]> {
  const map = new Map<string, Instance[]>();
  for (const i of instances) {
    const key = `${i.person_id}|${i.dimension_id}`;
    const list = map.get(key) ?? [];
    list.push(i);
    map.set(key, list);
  }
  return map;
}

export function standingKey(personId: string, dimensionId: string): string {
  return `${personId}|${dimensionId}`;
}

// Only events logged at/after the read anchor count toward the score.
export function eventsSinceAnchor(
  events: Instance[],
  anchor: number,
): Instance[] {
  return events.filter((e) => e.created_at >= anchor);
}
